import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../Button/Button';
import { useLanguage } from '../../contexts/LanguageContext';
import type { StaffReassignmentConflictDetails } from '../../hooks/useStaff';
import styles from './BookingConflictBlockBanner.module.css';

/**
 * AB#1862 — Hard-block dialog shown when the BE rejects a `DeleteStaff`
 * with `STAFF_REASSIGN_HAS_ACTIVE_BOOKINGS`. Per Faris's arch-brief §2.3,
 * Delete is the only true HARD block — there is no `forceWithActiveBookings`
 * override path because a deleted staff row would drag appointments into
 * FK-orphan land. The owner must cancel or reassign the bookings first.
 *
 * Two actions only:
 *   - Got it (close) — keeps the staff row + bookings intact.
 *   - Go to Calendar — deep-link to /calendar?staffId=... (forward-compatible
 *                       hint; CalendarPage today reads branchId only).
 *
 * Same overlay/shell shape as the confirm dialog so the visual continuity
 * is preserved; the divergent color (red) signals that this is a true
 * block, not an override-able warning.
 */
export interface BookingConflictBlockBannerProps {
    isOpen: boolean;
    details: StaffReassignmentConflictDetails;
    staffName: string;
    onClose: () => void;
}

function formatEarliestDate(iso: string, language: 'en' | 'ar'): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    try {
        const locale = language === 'ar' ? 'ar-EG' : 'en-US';
        return new Intl.DateTimeFormat(locale, {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(date);
    } catch {
        return iso;
    }
}

function interpolate(template: string, vars: Record<string, string | number>): string {
    return Object.keys(vars).reduce(
        (acc, key) => acc.split(`{${key}}`).join(String(vars[key])),
        template,
    );
}

const BookingConflictBlockBanner: React.FC<BookingConflictBlockBannerProps> = ({
    isOpen,
    details,
    staffName,
    onClose,
}) => {
    const { t, language } = useLanguage();
    const navigate = useNavigate();

    if (!isOpen) return null;

    const earliestPretty = formatEarliestDate(details.earliestAppointmentDate, language);
    const bodyCopy = interpolate(t('staff.reassign.conflict.deleteBody'), {
        count: details.count,
        earliest: earliestPretty,
    });

    /**
     * Deep-link to the owner Calendar. CalendarPage today reads only
     * `branchId` / `day` / `apptId`; we pass `staffId` and `day` (the
     * earliest affected date) as forward-compatible params. A follow-up
     * PBI can wire `staffId` into the grid filter; until then the deep-link
     * at least lands the owner on the right calendar day.
     */
    const handleGoToCalendar = () => {
        const params = new URLSearchParams();
        const earliestDay = details.earliestAppointmentDate.slice(0, 10);
        if (earliestDay) params.set('day', earliestDay);
        navigate(`/calendar?${params.toString()}`);
    };

    return (
        <div
            className={`${styles.modalOverlay} ${styles.open}`}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="bc-block-title"
            aria-describedby="bc-block-body"
        >
            <div className={`${styles.modalContent} ${styles.open}`}>
                {/* Same close-X / labelled-button split as the confirm
                    dialog — see ConfirmBookingConflictDialog for the
                    accessible-name rationale. */}
                <button
                    type="button"
                    className={styles.closeButton}
                    onClick={onClose}
                    aria-label="Close"
                    data-testid="bc-block-close-x"
                >
                    &times;
                </button>
                <div className={styles.iconWrap} aria-hidden="true">!</div>
                <h2 id="bc-block-title" className={styles.header}>
                    {t('staff.reassign.conflict.deleteTitle')}
                </h2>
                <p id="bc-block-body" className={styles.body}>
                    <strong>{staffName}</strong>: {bodyCopy}
                </p>
                <button
                    type="button"
                    className={styles.calendarLink}
                    onClick={handleGoToCalendar}
                >
                    {t('staff.reassign.conflict.viewCalendar')} →
                </button>
                <div className={styles.buttonsContainer}>
                    <div>
                        {/* Single primary action — no force CTA exists for delete. */}
                        <Button
                            label={t('staff.reassign.conflict.deleteClose')}
                            onClick={onClose}
                            size="medium"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BookingConflictBlockBanner;
